import { INCOTERM_LIST } from "../../utils";

export interface TermOption {
  value: string;
  label: string;
}

/**
 * Stable i18n keys of the predefined payment methods. `PAYMENT_METHOD_LIST` is NOT used for the
 * labels: it is built with `i18next.t()` at import time, so its names can be frozen (or raw keys)
 * if i18n was not initialised yet. Callers translate these with the live `t` hook instead.
 */
export const PAYMENT_METHOD_KEYS = [
  "check",
  "e_check",
  "transfer",
  "cash",
] as const;

/**
 * Payment-method options. The stored value is the localized label, the same convention the
 * seller-side offer form and `paymentMethodInCurrentLocale` use.
 */
export const buildPaymentMethodOptions = (
  t: (key: string) => string
): TermOption[] =>
  PAYMENT_METHOD_KEYS.map((k) => {
    const label = t(`paymentMethods.${k}`);
    return { value: label, label };
  });

/** Incoterm options (plain codes, no translation involved). */
export const INCOTERM_OPTIONS: TermOption[] = INCOTERM_LIST.map((i) => ({
  value: i.name,
  label: i.name,
}));

/**
 * Prepends `current` as an extra option when it is non-blank and not already among `options`, so a
 * legacy free-text value on an existing OC stays selected instead of being silently dropped.
 */
export const withCurrentValue = (
  options: TermOption[],
  current: string
): TermOption[] =>
  current !== "" && !options.some((o) => o.value === current)
    ? [{ value: current, label: current }, ...options]
    : options;
